// FILE: packages/components/src/project/RoadmapProgress.tsx

import React from 'react';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import { CheckCircle2, Loader, CircleDashed } from 'lucide-react';

export interface RoadmapItem {
  text: string;
}

export interface RoadmapPhase {
  title: string;
  status: 'Complete' | 'In Progress' | 'Planned';
  items: RoadmapItem[];
}

interface RoadmapProgressProps {
  phases: RoadmapPhase[];
}

const getStatusAttributes = (status: RoadmapPhase['status']) => {
  switch (status) {
    case 'Complete':
      return {
        Icon: CheckCircle2,
        color: 'text-emerald-500',
        bgColor: 'bg-emerald-50/50',
        borderColor: 'border-emerald-200',
      };
    case 'In Progress':
      return {
        Icon: Loader,
        color: 'text-sky-500',
        bgColor: 'bg-sky-50/50',
        borderColor: 'border-sky-200',
      };
    case 'Planned':
    default:
      return {
        Icon: CircleDashed,
        color: 'text-slate-400',
        bgColor: 'bg-slate-100/50',
        borderColor: 'border-slate-200',
      };
  }
};

export const RoadmapProgress: React.FC<RoadmapProgressProps> = ({ phases }) => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15, delayChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { ease: 'easeOut', duration: 0.4 } },
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      className="grid grid-cols-1 md:grid-cols-3 gap-8"
    >
      {phases.map((phase, index) => {
        const { Icon, color, bgColor, borderColor } = getStatusAttributes(phase.status);
        return (
          <motion.div
            key={index}
            variants={itemVariants}
            className={clsx('p-6 rounded-xl border-2', bgColor, borderColor)}
          >
            <div className="flex items-center gap-3">
              <Icon className={clsx(color, phase.status === 'In Progress' && 'animate-spin')} size={22} />
              <h3 className="font-bold text-lg text-gray-900">{phase.title}</h3>
            </div>
            <p className={clsx('text-sm font-semibold mt-1 ml-[34px]', color)}>{phase.status}</p>
            <ul className="mt-4 space-y-2.5 list-disc list-inside text-slate-700 marker:text-slate-300">
              {phase.items.map((item, itemIndex) => (
                <li key={itemIndex}>{item.text}</li>
              ))}
            </ul>
          </motion.div>
        );
      })}
    </motion.div>
  );
};